"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { startContextualChat, type ChatContextType } from "@/lib/client-api";
import { isUsableWebSession, useAuthStore } from "@/lib/auth-store";
import { authHrefFor, sanitizeNextPath } from "@/lib/navigation";
import { useMounted } from "@/lib/use-mounted";

type ChatCTAProps = {
  children: React.ReactNode;
  /** User id of the person the conversation is with. */
  targetUserId?: string | null;
  contextType: ChatContextType;
  contextTitle: string;
  contextId?: string;
  /** Where the CTA should land when the target user id is unknown or auth is needed. */
  fallbackNext: string;
  authContext: string;
};

/**
 * Chat-first CTA. Signed-in users get the conversation created (with a
 * templated context message) and land inside it; signed-out users go through
 * /auth with a preserved next path. When the target user id is unknown the
 * link falls back to the plain messages deep link.
 */
export function ChatCTA({
  children,
  targetUserId,
  contextType,
  contextTitle,
  contextId,
  fallbackNext,
  authContext,
}: ChatCTAProps) {
  const router = useRouter();
  const mounted = useMounted();
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const demoMode = useAuthStore((state) => state.demoMode);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const hasSession = mounted ? isUsableWebSession({ user, accessToken, demoMode }) : false;
  const safeNext = sanitizeNextPath(fallbackNext);

  if (!hasSession) {
    return (
      <Link href={authHrefFor(safeNext, authContext)} className="btn btn-primary">
        {children}
      </Link>
    );
  }

  if (!targetUserId || demoMode) {
    return (
      <Link href={safeNext} className="btn btn-primary">
        {children}
      </Link>
    );
  }

  const open = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { href } = await startContextualChat({
        targetUserId,
        contextType,
        contextTitle,
        contextId,
      });
      router.push(href);
    } catch {
      setError("Could not open the chat. Try again from Messages.");
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="btn btn-primary" onClick={open} disabled={busy}>
        {busy ? "Opening chat..." : children}
      </button>
      {error ? <div className="notice notice-error">{error}</div> : null}
    </>
  );
}
